import { queryClient } from '@/context/CombinedProvider';
import {
  IAddToCartResponse,
  ICheckoutItem,
  IGetCartResponse,
  IMutationQuery,
  IQuery,
  IApplicableCouponsResponse,
  ICheckoutResponse,
  SelectedPaymentMode,
} from '@/types';
import {
  decodeAuthToken,
  ordersAxiosInstance,
} from '@/utils';
import { AxiosError } from 'axios';
import { v4 as uuidv4 } from 'uuid';

/**
 * Generates an idempotent key for the user's cart, valid for 10 minutes.
 * Uses Web Crypto API for hashing.
 *
 * @param userId - The ID of the user.
 * @param cartItems - The list of items in the cart.
 * @returns A Promise resolving to the idempotent key (SHA-256 hash in hex).
 */
// async function generateIdempotentKey(
//   userId: string,
//   cartItems: { id: string; quantity: number }[],
// ): Promise<string> {
//   // Round timestamp to the nearest 10-minute interval
//   const timestamp = Math.floor(Date.now() / (10 * 60 * 1000));

//   // sort so that the order is consistent
//   const cartString = JSON.stringify(
//     cartItems.sort((a, b) => a.id.localeCompare(b.id)),
//   );

//   // Concatenate user ID, cart contents, and timestamp
//   const data = `${userId}:${cartString}:${timestamp}`;

//   // Convert to Uint8Array for hashing
//   const encoder = new TextEncoder();
//   const dataBuffer = encoder.encode(data);

//   // Compute SHA-256 hash using Web Crypto API
//   const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);

//   // Convert hash to hex string
//   return Array.from(new Uint8Array(hashBuffer))
//     .map((byte) => byte.toString(16).padStart(2, '0'))
//     .join('');
// }

/**
 * Utility function to update cart query cache and optionally revalidate in background
 * Only updates cache if the response is valid
 */
const updateCartCache = async (
  cartResponse: IGetCartResponse | IAddToCartResponse,
  revalidate = true,
  invalidateApplicableCoupons = false,
  skipWallet = false,
) => {
  try {
    const { d2cUserId, organisationId } = decodeAuthToken() ?? {};
    const queryKey = getCart(skipWallet).queryKey;

    // Validate that we have a proper cart response
    if (!cartResponse || typeof cartResponse !== 'object') {
      window.dev.e('Invalid cart response, skipping cache update:', cartResponse);
      return;
    }

    // Update the cache immediately with the response data
    const cartData: IGetCartResponse = {
      cart: 'cart' in cartResponse ? cartResponse.cart : null,
      paymentOptions:
        'paymentOptions' in cartResponse
          ? cartResponse.paymentOptions || []
          : [],
      message: 'message' in cartResponse ? cartResponse.message : '',
    };

    // Additional validation for the cart data structure
    if (!('cart' in cartData) || !('message' in cartData)) {
      window.dev.e('Cart response missing required fields, skipping cache update:', cartData);
      return;
    }

    queryClient.setQueryData(queryKey, cartData);

    // Optionally revalidate in background to ensure data consistency
    if (revalidate) {
      queryClient.invalidateQueries({
        queryKey,
        refetchType: 'none' // Don't refetch immediately, just mark as stale
      });

      // Only invalidate applicable coupons when cart contents change (not for coupon apply/remove)
      if (invalidateApplicableCoupons) {
        queryClient.invalidateQueries({
          queryKey: ['applicable-coupons', organisationId, d2cUserId],
          refetchType: 'none'
        });
      }
    }
  } catch (error) {
    window.dev.e('Error updating cart cache:', error);
    // Don't throw here to avoid breaking the mutation flow
  }
};

export const getCart = (skipWallet?: boolean): IQuery<IGetCartResponse> => {
  const { d2cUserId, organisationId } = decodeAuthToken() ?? {};
  return {
    queryKey: [
      'cart',
      organisationId,
      d2cUserId,
      skipWallet ? 'skipWallet' : null,
    ].filter(Boolean),
    queryFn: async () => {
      const url = skipWallet ? '/v2/cart/?skipWallet=true' : '/v2/cart/';
      const response = await ordersAxiosInstance.get<IGetCartResponse>(url);
      return response.data;
    }
  }
}

export const addToCart = (): IMutationQuery<IAddToCartResponse> => {
  const { d2cUserId, organisationId } = decodeAuthToken() ?? {};
  return {
    mutationKey: ['add-to-cart', organisationId, d2cUserId],
    // @ts-expect-error - MutationFunction type mismatch with unknown parameter
    mutationFn: async (items: Array<ICheckoutItem>): Promise<IAddToCartResponse> => {
      try {
        const payload = { items };
        const response = await ordersAxiosInstance.patch<IAddToCartResponse>(
          '/v2/cart/',
          payload,
        );

        // Only update cache if the response is successful and has data
        if (response.data && response.status >= 200 && response.status < 300) {
          await updateCartCache(response.data, true, true); // Invalidate applicable coupons when adding items
        }

        return response.data;
      } catch (error) {
        // Don't update cache on error, let the error propagate
        console.error('Add to cart failed:', error);
        throw error;
      }
    }
  }
}

export const removeFromCart = (): IMutationQuery<IGetCartResponse> => {
  const { d2cUserId, organisationId } = decodeAuthToken() ?? {};
  return {
    mutationKey: ['remove-from-cart', organisationId, d2cUserId],
    // @ts-expect-error - MutationFunction type mismatch with unknown parameter
    mutationFn: async (itemId: string): Promise<IGetCartResponse> => {
      try {
        const response = await ordersAxiosInstance.delete<IGetCartResponse>(`/v2/cart/items/${itemId}`);

        // Only update cache if the response is successful and has data
        if (response.data && response.status >= 200 && response.status < 300) {
          await updateCartCache(response.data, true, true); // Invalidate applicable coupons when removing items
        }

        return response.data;
      } catch (error) {
        // Don't update cache on error, let the error propagate
        console.error('Remove from cart failed:', error);
        throw error;
      }
    }
  }
}

export const clearCart = (): IMutationQuery<IGetCartResponse> => {
  const { d2cUserId, organisationId } = decodeAuthToken() ?? {};
  return {
    mutationKey: ['clear-cart', organisationId, d2cUserId],
    mutationFn: async (): Promise<IGetCartResponse> => {
      try {
        const response = await ordersAxiosInstance.delete<IGetCartResponse>('/v2/cart/');

        // Only update cache if the response is successful and has data
        if (response.data && response.status >= 200 && response.status < 300) {
          // Ensure we pass an empty cart to cache update for clear cart operation
          const emptyCartResponse: IGetCartResponse = {
            cart: null,
            paymentOptions: [],
            message: response.data.message || 'Cart cleared successfully',
          };
          await updateCartCache(emptyCartResponse, true, true); // Invalidate applicable coupons when clearing cart
        }

        return response.data;
      } catch (error) {
        // Don't update cache on error, let the error propagate
        console.error('Clear cart failed:', error);
        throw error;
      }
    }
  }
}

export const applyCoupon = (skipWallet?: boolean): IMutationQuery<IGetCartResponse> => {
  const { d2cUserId, organisationId } = decodeAuthToken() ?? {};
  return {
    mutationKey: ['apply-coupon', organisationId, d2cUserId, skipWallet],
    // @ts-expect-error - MutationFunction type mismatch with unknown parameter
    mutationFn: async (couponCode: string): Promise<IGetCartResponse> => {
      try {
        const payload = { couponCode };
        const response = await ordersAxiosInstance.post<IGetCartResponse>(
          skipWallet ? '/v2/cart/coupon?skipWallet=true' : '/v2/cart/coupon',
          payload,
        );

        // Only update cache if the response is successful and has data
        if (response.data && response.status >= 200 && response.status < 300) {
          await updateCartCache(response.data); // Don't invalidate applicable coupons when applying coupon
        }

        return response.data;
      } catch (error) {
        if (error instanceof AxiosError && error.response?.data) {
          window.dev.e('Apply coupon failed:', error.response.data);
          throw error.response.data;
        }
        throw error;
      }
    }
  }
}

export const removeCoupon = (skipWallet?: boolean): IMutationQuery<IGetCartResponse> => {
  const { d2cUserId, organisationId } = decodeAuthToken() ?? {};
  return {
    mutationKey: ['remove-coupon', organisationId, d2cUserId, skipWallet],
    mutationFn: async (): Promise<IGetCartResponse> => {
      try {
        const response =
          await ordersAxiosInstance.delete<IGetCartResponse>(skipWallet ? '/v2/cart/coupon?skipWallet=true' : '/v2/cart/coupon');

        // Only update cache if the response is successful and has data
        if (response.data && response.status >= 200 && response.status < 300) {
          await updateCartCache(response.data); // Don't invalidate applicable coupons when removing coupon
        }

        return response.data;
      } catch (error) {
        // Don't update cache on error, let the error propagate
        console.error('Remove coupon failed:', error);
        throw error;
      }
    }
  }
}

export const getApplicableCoupons = (): IQuery<IApplicableCouponsResponse> => {
  const { d2cUserId, organisationId } = decodeAuthToken() ?? {};
  return {
    queryKey: ['applicable-coupons', organisationId, d2cUserId],
    queryFn: async () => {
      const response = await ordersAxiosInstance.get<IApplicableCouponsResponse>('/v2/cart/coupons');
      return response.data;
    }
  }
}

export const clearAndAddToCart = (): IMutationQuery<IAddToCartResponse> => {
  const { d2cUserId, organisationId } = decodeAuthToken() ?? {};
  return {
    mutationKey: ['clear-and-add-to-cart', organisationId, d2cUserId],
    // @ts-expect-error - MutationFunction type mismatch with unknown parameter
    mutationFn: async (item: ICheckoutItem): Promise<IAddToCartResponse> => {
      try {
        const items = [item];
        const payload = { items };
        const response = await ordersAxiosInstance.post<IAddToCartResponse>('/v2/cart/clear-and-add', payload);

        // Only update cache if the response is successful and has data
        // Don't mark as stale since we just got fresh data from the API
        if (response.data && response.status >= 200 && response.status < 300) {
          await updateCartCache(response.data, false, true); // Don't revalidate - we have fresh data, but invalidate coupons
        }

        return response.data;
      } catch (error) {
        // Don't update cache on error, let the error propagate
        window.dev.e('Clear and add to cart failed:', error);
        throw error;
      }
    }
  }
}

export const checkoutCart = (): IMutationQuery<ICheckoutResponse> => {
  const { d2cUserId, organisationId } = decodeAuthToken() ?? {};
  return {
    mutationKey: ['checkout-cart', organisationId, d2cUserId],
    // @ts-expect-error - MutationFunction type mismatch with unknown parameter
    mutationFn: async (
      selectedPaymentModes: SelectedPaymentMode[],
    ): Promise<ICheckoutResponse> => {
      try {
        // Get current cart data to generate idempotent key
        const cartData = queryClient.getQueryData<IGetCartResponse>(['cart', organisationId, d2cUserId]);

        // Generate idempotent key based on current cart items
        const cartItems = cartData?.cart?.items || [];
        // const idempotentKey = await generateIdempotentKey(
        //   d2cUserId ?? '',
        //   cartItems.map((item) => ({
        //     id: item.id,
        //     quantity: item.quantity,
        //   })),
        // );
        const idempotentKey = uuidv4();

        const payload = {
          selectedPaymentModes: selectedPaymentModes || [],
        };

        window.dev.i('Checkout cart with idempotent key', { cartItems, idempotentKey, selectedPaymentModes });

        const response = await ordersAxiosInstance.post<ICheckoutResponse>(
          '/v2/cart/checkout',
          payload,
          {
            headers: {
              ['x-idempotent-key']: idempotentKey,
            },
          },
        );
        return response.data;
      } catch (error) {
        console.error('Cart checkout failed:', error);
        throw error;
      }
    }
  }
} 